import { isStartBlock } from '@/util/blocks';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { useSubroutine } from '@/hooks/useSubroutine';
import { BackendNode, BackendNodeId } from '@/types/backendNode';
import { Detail } from '@/types/detail';
import { Configuration, ConfigurationEnum, ConfigurationMultiple } from '@/types/metadata/metadata';
import { KeyValue, Variable } from '@/types/variable';
import { findSubroutine, getSubroutineByNode } from '@/util/application';
import {
  isArray,
  isConfigurationEnum,
  isConfigurationMultiple,
  isKeyValue,
  isNullOrUndefined,
  isNumber,
  isObject,
  isString,
  isVariable
} from '@/util/typeGuards';
import { synceventUtil } from '@/util/validation/syncevent';
import { userEventUtil } from '@/util/validation/userevent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import {
  Box,
  Card,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  useTheme
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, MouseEvent, useEffect, useState } from 'react';

type BlockSearchType = {
  blockId: string;
  block: BackendNode;
  currentSubroutineLabel: string;
  searchValue: string[];
}[];

type SearchBlocksDialogProps = {
  open: boolean;
  onClose: () => void;
  search: (blockId: string | undefined) => void;
};

const defaultMaxResults = 50;

const SearchBlocksDialog: FC<SearchBlocksDialogProps> = (props) => {
  const { application } = useApplication();
  const { metadata } = useMetadata();
  const { selectedSubroutineId } = useSubroutine();
  const theme = useTheme();

  const [count, setCount] = useState(0);
  const [selectedBlock, setSelectedBlock] = useState<string>();
  const [blockQuery, setBlockQuery] = useState('');
  const [searchType, setSearchType] = useState('full');
  const [listedBlocks, setListedBlocks] = useState<BlockSearchType>();
  const [maxResults, setMaxResults] = useState(defaultMaxResults);
  const [currentSubOnly, setCurrentSubOnly] = useState<boolean>(false);
  const [exactSearch, setExactSearch] = useState<boolean>(false);

  const matchesQuery = (value: string | undefined): boolean | undefined => {
    if (!value) return;

    const localeValue = value.toString().toLocaleLowerCase();

    return exactSearch
      ? localeValue === blockQuery.toLocaleLowerCase()
      : localeValue.includes(blockQuery.toLocaleLowerCase());
  };

  const handleSearch = (e: MouseEvent<HTMLButtonElement | HTMLDivElement, globalThis.MouseEvent>, blockId?: string) => {
    props.search(blockId || selectedBlock);
    props.onClose();
  };

  const findPrompt = (prompt: Variable | string) => {
    if (isNumber(prompt)) {
      const currentPrompt = application.prompts.find(({ id }) => id === prompt);

      if (!currentPrompt) return;

      const result = [];

      matchesQuery(currentPrompt.label) && result.push(`Prompt Label: ${currentPrompt.label}`);
      matchesQuery(currentPrompt.file) && result.push(`Prompt File: ${currentPrompt.file}`);
      if (isObject(currentPrompt.path)) {
        matchesQuery(currentPrompt.path.name) && result.push(`Prompt Path: ${currentPrompt.file}`);
      }
      if (isString(currentPrompt.path)) {
        matchesQuery(currentPrompt.path) && result.push(`Prompt Path: ${currentPrompt.file}`);
      }

      const currentPromptGroup = application.promptGroups?.find(
        (promptGroup) => promptGroup.id === currentPrompt.promptGroup
      );
      if (!currentPromptGroup) return result;

      matchesQuery(currentPromptGroup.name) && result.push(`Prompt Group: ${currentPromptGroup.name}`);

      return result;
    }

    return;
  };

  const recursivePromptSections = (config: ConfigurationEnum, blockId: string): string[] => {
    let result: string[] = [];
    config.sections?.find((section) => {
      section.forEach((innerSection) => {
        const sectionDetailValue = application.blocks.details[blockId][innerSection.value];
        if (innerSection.type === 'prompts' && isArray(sectionDetailValue)) {
          sectionDetailValue.forEach((prompt: Variable | string) => {
            const foundPrompt = findPrompt(prompt);
            foundPrompt && foundPrompt.forEach((prompt) => result.push(prompt));
          });
        }
        if (innerSection.type === 'kvvaluelist' && isArray(sectionDetailValue)) {
          sectionDetailValue.forEach((sectionValue: KeyValue<string | number> | string) => {
            if (isKeyValue(sectionValue) && isArray(sectionValue.value)) {
              sectionValue.value.forEach((promptValue) => {
                const foundPrompt = findPrompt(promptValue);
                foundPrompt && foundPrompt.forEach((prompt) => !result.includes(prompt) && result.push(prompt));
              });
            }
          });
        }
        if (innerSection.sections) {
          result = recursivePromptSections(innerSection, blockId);
        }
      });
    });

    return result;
  };

  const searchForPrompts = (blockId: string): string[] => {
    const result: string[] = [];
    if (!application.blocks.details[blockId]) return result;
    metadata.blocks
      .find((block) => block.type === application.blocks.nodes[blockId].type)
      ?.configuration.forEach((config) => {
        const configDetailValue = application.blocks.details[blockId][config.value];
        if (config.type === 'prompts' && isArray(configDetailValue)) {
          configDetailValue.forEach((prompt: Variable | string) => {
            const foundPrompt = findPrompt(prompt);
            foundPrompt && foundPrompt.forEach((prompt) => result.push(prompt));
          });
        }
        if (isConfigurationEnum(config) && config.sections) {
          const foundSection = recursivePromptSections(config, blockId);
          foundSection && foundSection.forEach((prompt) => result.push(prompt));
        }
      });

    return result;
  };

  const searchForNotes = (blockId: string): string => {
    const noteText = application.blocks.details[blockId] && application.blocks.details[blockId]['note'];
    if (isString(noteText) && matchesQuery(noteText)) {
      return `Text: ${noteText}`;
    }

    return '';
  };

  const findVariable = (detail: unknown): string => {
    let result = '';
    if (isVariable(detail)) {
      if (matchesQuery(detail.name)) {
        result = detail.name;
      }
    } else if (isObject(detail)) {
      Object.entries(detail).forEach(([_, subDetailValue]) => {
        const foundVariable = findVariable(subDetailValue);
        if (foundVariable) {
          result = foundVariable;
        }
      });
    } else if (isArray(detail)) {
      detail.map((subDetail) => {
        const foundVariable = findVariable(subDetail);
        if (foundVariable) {
          result = foundVariable;
        }
      });
    }

    return result;
  };

  const searchForVariables = (blockId: string): string[] => {
    const result: string[] = [];
    if (application.blocks.details[blockId]) {
      Object.entries(application.blocks.details[blockId]).forEach(([_, detail]) => {
        const foundVariable = findVariable(detail);
        foundVariable && result.push(`Variable Name: ${foundVariable}`);
      });
    }

    application.subroutines.map((subroutine) => {
      if (subroutine.startblock?.toString() === blockId) {
        [...subroutine.arg_bindings, ...(subroutine.event_arg_bindings ?? [])].forEach((binding) => {
          matchesQuery(binding) && result.push(`Variable Name: ${binding}`);
        });
      }
    });

    return result;
  };

  const findValue = (config: Configuration, blockId: string, isMultiple: boolean): string[] => {
    const result: string[] = [];
    if (!application.blocks.details[blockId]) return result;

    if (config.type === 'value' || config.type === 'textblock') {
      const foundValue = application.blocks.details[blockId][config.value];
      if (foundValue && !isVariable(foundValue)) {
        if (matchesQuery(String(foundValue))) {
          result.push(`Value: ${foundValue}`);
        }
      }
      if (!foundValue && isMultiple) {
        const multiple = application.blocks.details[blockId]['multiple'];
        if (isArray(multiple)) {
          multiple.forEach((value: Detail) => {
            const multipleValue = value[config.value];
            if (
              !isNullOrUndefined(multipleValue) &&
              !isVariable(multipleValue) &&
              matchesQuery(String(multipleValue))
            ) {
              result.push(`Value: ${multipleValue}`);
            }
          });
        }
      }
    }
    if (
      config.type === 'returnvalues' ||
      config.type === 'appreturnvalues' ||
      config.type === 'arguments' ||
      config.type === 'valuelist'
    ) {
      const foundValue = application.blocks.details[blockId][config.value];
      if (isArray(foundValue)) {
        foundValue.forEach((value) => {
          if (!isVariable(foundValue) && matchesQuery(value)) {
            result.push(`Value: ${value}`);
          }
        });
      }
    }
    if (config.type === 'kvvaluelist' || config.type === 'kvvariableslist') {
      const foundValue = application.blocks.details[blockId][config.value];
      if (isArray(foundValue)) {
        foundValue.forEach((value) => {
          if (isKeyValue(value)) {
            if (matchesQuery(value.key)) {
              result.push(`Key: ${value.key}`);
            }
            if (!isVariable(value.value) && matchesQuery(value.value)) {
              result.push(`Value: ${value.value}`);
            }
          }
        });
      }
    }

    return result;
  };

  const recursiveValueSections = (
    config: ConfigurationEnum | ConfigurationMultiple,
    blockId: string,
    isMultiple: boolean
  ): string[] => {
    const result: string[] = [];
    config.sections?.forEach((section) => {
      if (isObject(section)) {
        const foundValue = findValue(section, blockId, isMultiple);
        foundValue.forEach((value) => !result.includes(value) && result.push(value));

        if (isConfigurationEnum(section) && section.sections) {
          const foundRecursiveValue = recursiveValueSections(section, blockId, isMultiple);
          foundRecursiveValue.forEach((value) => !result.includes(value) && result.push(value));
        }
      }
      if (isArray(section)) {
        section.find((innerSection) => {
          const foundValue = findValue(innerSection, blockId, isMultiple);
          foundValue.forEach((value) => !result.includes(value) && result.push(value));

          if (innerSection.sections) {
            const foundInnerSectionValue = recursiveValueSections(innerSection, blockId, isMultiple);
            foundInnerSectionValue.forEach((value) => !result.includes(value) && result.push(value));
          }
        });
      }
    });

    return result;
  };

  const searchForValues = (blockId: string): string[] => {
    const result: string[] = [];

    metadata.blocks
      .find((block) => block.type === application.blocks.nodes[blockId].type)
      ?.configuration.forEach((config) => {
        const foundValue = findValue(config, blockId, false);
        foundValue.forEach((value) => !result.includes(value) && result.push(value));

        if ((isConfigurationEnum(config) || isConfigurationMultiple(config)) && config.sections) {
          const foundSection = recursiveValueSections(config, blockId, isConfigurationMultiple(config));
          foundSection.forEach((value) => !result.includes(value) && result.push(value));
        }
      });

    if (isStartBlock(application.blocks.nodes[blockId])) {
      const subroutine = getSubroutineByNode(application, blockId);
      subroutine?.args?.forEach((arg) => {
        if (matchesQuery(arg.label)) {
          result.push(`Label: ${arg.label}`);
        }
      });
      subroutine?.return_types.forEach((returnType) => {
        if (matchesQuery(returnType.label)) {
          result.push(`Label: ${returnType.label}`);
        }
      });
    }
    if (application.blocks.event_handlers && application.blocks.event_handlers[blockId]) {
      Object.entries(application.blocks.event_handlers[blockId]).forEach(([_, handler]) => {
        handler.arguments?.forEach((argument) => {
          if (!isVariable(argument)) {
            if (matchesQuery(argument?.toString())) {
              result.push(`Value: ${argument}`);
            }
          }
        });
      });
    }

    return result;
  };

  const findEvent = (config: Configuration, blockId: string, isMultiple: boolean): string[] => {
    const result: string[] = [];

    if (config.type !== 'userevent' && config.type !== 'syncevent') return result;

    const details = application.blocks.details[blockId];
    if (!details) return result;

    const matchUserEvent = (eventName: string) => {
      matchesQuery(eventName) && result.push(`User Event: ${eventName}`);
      const userEvent = application.userevents?.find(({ event }) => event === eventName);
      userEvent && matchesQuery(userEvent.label) && result.push(`User Event: ${userEvent.label}`);
    };

    const matchSyncEvent = (eventName: string) => {
      matchesQuery(eventName) && result.push(`Sync Event: ${eventName}`);
      const syncEvent = application.syncevents?.find(({ name }) => name === eventName);
      if (!syncEvent) return;
      const stateLabel = metadata.synceventstates?.find(({ state }) => state === syncEvent.state)?.label;
      (matchesQuery(syncEvent.state) || matchesQuery(stateLabel)) &&
        result.push(`Sync State: ${stateLabel || syncEvent.state}`);
    };

    const getEventName = config.type === 'userevent' ? userEventUtil().getValue : synceventUtil().getValue;
    const matchEvent = config.type === 'userevent' ? matchUserEvent : matchSyncEvent;

    const eventName = getEventName(config, details);
    if (eventName) matchEvent(eventName);

    if (!eventName && isMultiple && isArray(details['multiple'])) {
      details['multiple'].forEach((entry: Detail) => {
        const multipleName = getEventName(config, entry);
        if (multipleName) matchEvent(multipleName);
      });
    }

    return result;
  };

  const recursiveEventSections = (
    config: ConfigurationEnum | ConfigurationMultiple,
    blockId: string,
    isMultiple: boolean
  ): string[] => {
    const result: string[] = [];
    config.sections?.forEach((section) => {
      if (isObject(section)) {
        const foundEvent = findEvent(section, blockId, isMultiple);
        foundEvent.forEach((event) => !result.includes(event) && result.push(event));

        if (isConfigurationEnum(section) && section.sections) {
          const foundRecursiveEvent = recursiveEventSections(section, blockId, isMultiple);
          foundRecursiveEvent.forEach((event) => !result.includes(event) && result.push(event));
        }
      }
      if (isArray(section)) {
        section.forEach((innerSection) => {
          const foundEvent = findEvent(innerSection, blockId, isMultiple);
          foundEvent.forEach((event) => !result.includes(event) && result.push(event));

          if (innerSection.sections) {
            const foundInnerSectionEvent = recursiveEventSections(innerSection, blockId, isMultiple);
            foundInnerSectionEvent.forEach((event) => !result.includes(event) && result.push(event));
          }
        });
      }
    });

    return result;
  };

  const searchForEvents = (blockId: string): string[] => {
    const result: string[] = [];

    metadata.blocks
      .find((block) => block.type === application.blocks.nodes[blockId].type)
      ?.configuration.forEach((config) => {
        const foundEvent = findEvent(config, blockId, false);
        foundEvent.forEach((event) => !result.includes(event) && result.push(event));

        if ((isConfigurationEnum(config) || isConfigurationMultiple(config)) && config.sections) {
          const foundSection = recursiveEventSections(config, blockId, isConfigurationMultiple(config));
          foundSection.forEach((event) => !result.includes(event) && result.push(event));
        }
      });

    return result;
  };

  const searchForConnections = (blockId: string): string[] => {
    const result: string[] = [];
    application.blocks.connections[blockId] &&
      Object.entries(application.blocks.connections[blockId])?.find(([_, connection]) => {
        matchesQuery(connection.label) && result.push(`Label: ${connection.label}`);
        matchesQuery(connection.action) && result.push(`Action: ${connection.action}`);
      });
    return result;
  };

  const searchForSubroutines = (blockId: string, block: BackendNode): string[] => {
    const result = [];

    if (application.blocks.event_handlers && application.blocks.event_handlers[blockId]) {
      Object.entries(application.blocks.event_handlers[blockId]).find(([_, eventHandler]) => {
        const subLabel = application.subroutines.find((sub) => sub.id === eventHandler.eventstart)?.label;

        matchesQuery(subLabel) && result.push(`Subroutine: ${subLabel}`);
      });
    }

    if (block.type === 'Gosub' || block.type === 'RunTask') {
      const substartId = application.blocks.details[blockId]['substart'];
      const linkedSubroutine = application.subroutines.find((subroutine) => substartId && subroutine.id === substartId);

      linkedSubroutine && matchesQuery(linkedSubroutine.label) && result.push(`Subroutine: ${linkedSubroutine.label}`);
    }

    return result;
  };

  const searchForBlocks = (blockId: string, block: BackendNode): string[] => {
    const result = [];

    matchesQuery(block.label) && result.push(`Label: ${block.label}`);
    matchesQuery(block.type) && result.push(`Type: ${block.type}`);
    matchesQuery(blockId) && result.push(`ID: ${blockId}`);

    if (application.blocks.event_handlers && application.blocks.event_handlers[blockId]) {
      Object.entries(application.blocks.event_handlers[blockId]).find(([_, eventHandler]) => {
        const subLabel = application.subroutines.find((sub) => sub.id === eventHandler.eventstart)?.label;
        matchesQuery(subLabel) && result.push(`Subroutine: ${subLabel}`);

        eventHandler.arguments.map((argument) => {
          const variable = findVariable(argument);
          variable && result.push(`Variable: ${variable}`);
        });
      });
    }

    if (block.type === 'Gosub' || block.type === 'RunTask') {
      const substartId = application.blocks.details[blockId]['substart'];
      const linkedSubroutine = substartId
        ? application.subroutines.find((subroutine) => subroutine.id === substartId)
        : undefined;

      linkedSubroutine && matchesQuery(linkedSubroutine.label) && result.push(`Subroutine: ${linkedSubroutine.label}`);
    }

    return result;
  };

  const currentSubNodes = () => {
    const currentSubroutine = findSubroutine(application.subroutines, selectedSubroutineId);
    const result: Record<BackendNodeId, BackendNode> = {};

    currentSubroutine?.nodes.forEach((node) => (result[node] = application.blocks.nodes[node]));

    return result;
  };

  useEffect(() => {
    if (!props.open) return;

    setCount(0);

    if (!blockQuery) {
      setListedBlocks(undefined);
      setSelectedBlock(undefined);
      return;
    }

    const searchNodes = currentSubOnly ? currentSubNodes() : application.blocks.nodes;
    const result: BlockSearchType = [];

    switch (searchType) {
      case 'blocks':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchBlocks = searchForBlocks(blockId, block);
          if (searchBlocks.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchBlocks });
          }
        });
        break;

      case 'connections':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchConnections = searchForConnections(blockId);
          if (searchConnections.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchConnections });
          }
        });
        break;

      case 'variables':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchVariables = searchForVariables(blockId);
          if (searchVariables.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchVariables });
          }
        });
        break;

      case 'values':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchValues = searchForValues(blockId);
          if (searchValues.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchValues });
          }
        });
        break;

      case 'prompt':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchPrompts = searchForPrompts(blockId);
          if (searchPrompts.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchPrompts });
          }
        });
        break;

      case 'notes':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          if (block.type !== 'Note') return;

          const searchNotes = searchForNotes(blockId);
          if (searchNotes) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: [searchNotes] });
          }
        });
        break;

      case 'events':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchEvents = searchForEvents(blockId);
          if (searchEvents.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchEvents });
          }
        });
        break;

      case 'subroutines':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchBlocks = searchForSubroutines(blockId, block);
          if (searchBlocks.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchBlocks });
          }
        });
        break;

      default:
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchBlocks = searchForBlocks(blockId, block);
          const searchVariables = searchForVariables(blockId);
          const searchValues = searchForValues(blockId);
          const searchPrompts = searchForPrompts(blockId);
          const searchConnections = searchForConnections(blockId);
          const searchNotes = searchForNotes(blockId);
          const searchEvents = searchForEvents(blockId);

          if (
            searchBlocks.length ||
            searchVariables.length ||
            searchValues.length ||
            searchPrompts.length ||
            searchConnections.length ||
            searchEvents.length ||
            searchNotes
          ) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({
                blockId,
                block,
                currentSubroutineLabel,
                searchValue: [
                  ...searchBlocks,
                  ...searchVariables,
                  ...searchValues,
                  ...searchPrompts,
                  ...searchConnections,
                  ...searchEvents,
                  searchNotes
                ]
              });
          }
        });
    }

    setListedBlocks(result);
  }, [props.open, searchType, blockQuery, maxResults, exactSearch, currentSubOnly]);

  useEffect(() => {
    setBlockQuery('');
    setSearchType('full');
  }, [application.applicationId]);

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth disableRestoreFocus>
      <DialogTitle>Search Blocks</DialogTitle>
      <DialogContent
        sx={{
          minHeight: theme.spacing(40),
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Card
          variant='outlined'
          sx={{
            bgcolor: 'transparent',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box display={'flex'} alignItems={'flex-center'} gap={1} pt={1}>
            <TextField
              label='Search'
              autoFocus={props.open}
              value={blockQuery}
              onChange={(event) => {
                setMaxResults(defaultMaxResults);
                setBlockQuery(event.target.value);
              }}
              sx={{ minWidth: theme.spacing(40) }}
              size='small'
              InputProps={{ autoComplete: 'off' }}
            />
            <TextField
              value={searchType}
              select
              size='small'
              label='Search type'
              onChange={(event) => {
                setMaxResults(defaultMaxResults);
                setSearchType(event.target.value);
              }}
              sx={{ flexGrow: 1, minWidth: '10rem' }}
              InputProps={{ autoComplete: 'off' }}
            >
              <MenuItem value={'full'}>Full</MenuItem>
              <MenuItem value={'blocks'}>Block</MenuItem>
              <MenuItem value={'variables'}>Variable</MenuItem>
              <MenuItem value={'values'}>Value</MenuItem>
              <MenuItem value={'prompt'}>Prompt</MenuItem>
              <MenuItem value={'connections'}>Actions</MenuItem>
              <MenuItem value={'notes'}>Notes</MenuItem>
              <MenuItem value={'events'}>Events</MenuItem>
              <MenuItem value={'subroutines'}>Subroutines</MenuItem>
            </TextField>
            <Tooltip title={'Exact Match'} placement='top'>
              <Box width={40}>
                <Box
                  position='absolute'
                  top={63}
                  right={40}
                  fontSize={10.275}
                  color='#ffffffb3'
                  letterSpacing={0.12}
                  fontFamily='"Roboto", "Helvetica", "Arial", sans-serif'
                >
                  Exact
                </Box>
                <Checkbox checked={exactSearch} onChange={(e, checked) => setExactSearch(checked)} color='default' />
              </Box>
            </Tooltip>
          </Box>
          <List
            sx={{
              maxHeight: theme.spacing(50),
              overflowY: 'auto',
              width: '100%'
            }}
          >
            {listedBlocks?.map((listedBlock) => {
              if (!listedBlock) return;
              const isSelected = listedBlock.blockId === selectedBlock;
              return (
                <ListItemButton
                  key={listedBlock.blockId}
                  selected={isSelected}
                  onClick={() => setSelectedBlock(listedBlock.blockId)}
                  onDoubleClick={(e) => handleSearch(e, listedBlock.blockId)}
                  sx={{ lineBreak: 'anywhere' }}
                >
                  <ListItemIcon sx={{ minWidth: '40px', alignItems: 'center' }}>
                    {isSelected ? (
                      <CheckCircleIcon
                        sx={{
                          color: 'lightgray'
                        }}
                      />
                    ) : (
                      <CircleIcon
                        sx={{
                          color: '#ffffff'
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <Box width='50%' display='flex' flexDirection='row' alignItems='center'>
                    <ListItemText
                      primary={
                        listedBlock.block.label
                          ? `${listedBlock.blockId} - ${listedBlock.block.label}`
                          : listedBlock.blockId
                      }
                      secondary={`${listedBlock.block.type} - ${listedBlock.currentSubroutineLabel}`}
                    />
                  </Box>
                  <ListItemText
                    primary={listedBlock.searchValue.map((searchValue, index) => (
                      <Box key={`searchValue ${index}`} my={'5px'}>
                        {searchValue}
                      </Box>
                    ))}
                    sx={{ display: 'flex', flexDirection: 'column', width: '40%' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Card>
        {count > 0 && (
          <ListItemText
            primary={`Total hits: ${count}`}
            secondary={maxResults === 99999 || count < maxResults ? undefined : 'Showing 50 first hits.'}
            sx={{ mt: '10px', mb: 0, ml: '5px' }}
          />
        )}
        <Tooltip title={'Search only in current subroutine'} placement='top'>
          <Box width={200} display='flex' flexDirection='row' alignItems='center'>
            <Checkbox
              checked={currentSubOnly}
              onChange={(e, checked) => setCurrentSubOnly(checked)}
              color='default'
              sx={{ p: '5px' }}
            />
            <Box
              fontSize={10.275}
              color='#ffffffb3'
              letterSpacing={0.12}
              fontFamily='"Roboto", "Helvetica", "Arial", sans-serif'
            >
              Search only in current subroutine
            </Box>
          </Box>
        </Tooltip>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {count > maxResults && (
            <Button variant='outlined' onClick={() => setMaxResults(99999)} color='secondary'>
              Show All
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={props.onClose} color='secondary'>
            Close
          </Button>
          <Button onClick={handleSearch} color='primary' variant='contained' disabled={!selectedBlock}>
            Go to
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SearchBlocksDialog;
